import React, { Fragment } from 'react';
import { ApolloError } from '@apollo/client';
import { ApolloErrorsObject, GENERIC_ERROR_MESSAGE } from '../constants/apollo-errors';
import { ServerError } from '@apollo/client/link/utils';
import { SQError } from '../models/basic-objects';
import {
  ApolloErrorCategoriesType,
  ApolloErrorMapKeysType,
  ApolloErrorMapTypesType,
  ApolloErrorResponseType,
  ApolloExtensionsType,
  ApolloOperationType
} from '../models/apollo-errors';

export type MatchedPropsTupleType =
  | ['response', null]
  | ['operation', ApolloOperationType | undefined]
  | ['category', ApolloErrorCategoriesType | undefined]
  | ['type', ApolloErrorMapTypesType | ApolloErrorMapTypesType[] | undefined];

const getNumberOfPropsInErrorObject = (object: ApolloErrorResponseType): number => {
  let total = 0;
  if (!!object.response) {
    total++;
  }
  if (object.operation) {
    total++;
  }
  if (object.extensions?.type) {
    total++;
  }
  if (object.extensions?.category) {
    total++;
  }
  if (object.extensions?.message) {
    total++;
  }
  return total;
};

// this is a recursive function that will narrow down matches to object that has the most matches. if no matches provided it resolves to generic error message
const getObjectResponse = (
  extensions: ApolloExtensionsType,
  operation: ApolloOperationType | undefined,
  properties: MatchedPropsTupleType[],
  errorObjectList: ApolloErrorResponseType[],
  defaultResponse: ApolloErrorResponseType = ApolloErrorsObject.GENERIC_ERROR_MESSAGE[0]
): JSX.Element => {
  // for the list op properties we grab the first and remove it from the array for next iteration
  const propertyTuple = properties[0];
  properties.shift();
  // we filter down errorObjectList matching the provided tuples
  const matchedList = errorObjectList.filter(object => {
    switch (propertyTuple[0]) {
      // we check if object has response key. response key is provided it sets new defaultResponse to that value
      // for all other props (operation, category, type) we check if key exists, and if values match. if no key provided, we return that value as matched
      case 'response':
        return !!object.response;
      case 'operation':
        return object?.operation ? propertyTuple[1] === object?.operation : true;
      case 'type':
        if (Array.isArray(object?.extensions?.type) && propertyTuple[1]) {
          return !!(object?.extensions?.type as ApolloErrorMapTypesType[]).find(value => value === propertyTuple[1]);
        }
        return object?.extensions?.type ? propertyTuple[1] === object?.extensions?.type : true;
      case 'category':
        return object?.extensions?.category ? propertyTuple[1] === object?.extensions?.category : true;
      default:
        return false;
    }
  });
  if (properties.length) {
    // if we still have props (keys to check, we call fnc with new values)
    return getObjectResponse(extensions, operation, properties, matchedList, defaultResponse);
  } else {
    // if all properties (keys) are exhausted and checked, we reduced the matched list to value that has the most matched props, and return that response
    const mostMatched = matchedList.reduce((acc, current) => {
      const matchedCurrentNumber = getNumberOfPropsInErrorObject(current);
      const matchedAccNumber = getNumberOfPropsInErrorObject(acc);
      return matchedCurrentNumber > matchedAccNumber || acc === defaultResponse ? current : acc;
    }, defaultResponse);
    if (mostMatched.response instanceof Function) {
      return mostMatched.response(extensions, operation);
    }
    return mostMatched.response;
  }
};

export const resolveErrorMapResponse = (
  errorObjectList: ApolloErrorResponseType[],
  operation: ApolloOperationType | undefined,
  extensions: ApolloExtensionsType
): JSX.Element => {
  const providedProperties: MatchedPropsTupleType[] = [
    ['response', null],
    ['operation', operation],
    ['type', extensions.type],
    ['category', extensions.category]
  ]; // it has to have response
  return getObjectResponse(extensions, operation, providedProperties, errorObjectList);
};

export const remapApolloErrors = (error: ApolloError): JSX.Element => {
  let response: JSX.Element | undefined;
  if (
    error.networkError?.hasOwnProperty('result') &&
    (error.networkError as ServerError).result.hasOwnProperty('errors')
  ) {
    // Server responses generally only have a single graphql error, so grab it from the first index.
    const graphqlError: SQError = (error.networkError as ServerError).result.errors[0];
    const errorMessage: ApolloErrorMapKeysType = graphqlError.message as ApolloErrorMapKeysType;

    // If the error code/message matches one in our apollo errors object, continue.
    if (ApolloErrorsObject.hasOwnProperty(errorMessage)) {
      const errorObjectList = ApolloErrorsObject[errorMessage];

      const extensions = graphqlError.extensions;
      const operation = graphqlError.path?.[0] || undefined; //always take the first path from the array. we make sure on backend we always set the path to operation

      // find object that corresponds to the graphqlError
      response = resolveErrorMapResponse(errorObjectList, operation, extensions);
    } else {
      // invalid key - revert to default generic message
      response = <>{GENERIC_ERROR_MESSAGE}</>;
    }
  } else {
    response = <>{error.message}</>;
  }

  return <Fragment>{response}</Fragment>;
};

export const getApolloErrorMessage = (error: ApolloError): ApolloErrorMapKeysType => {
  if (
    error.networkError?.hasOwnProperty('result') &&
    (error.networkError as ServerError).result.hasOwnProperty('errors')
  ) {
    const graphqlError: SQError = (error.networkError as ServerError).result.errors[0];
    return graphqlError.message as ApolloErrorMapKeysType;
  }
  return 'GENERIC_ERROR_MESSAGE';
};
